import React, { useState, useEffect } from 'react';
import { 
  Store, 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  CheckCircle,
  DollarSign,
  Eye,
  Grid3x3,
  List,
} from 'lucide-react';
import { shopService, Shop } from '../services/shopService';
import { customToast } from '../utils/customToast';
import { Button } from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';
import { useTitle } from '../hooks/useTitle';
import { format } from 'date-fns';
import CreateShopModal from '../components/modals/CreateShopModal';
import EditShopModal from '../components/modals/EditShopModal';
import ViewShopDetailsModal from '../components/modals/ViewShopDetailsModal';
import RecordPaymentModal from '../components/modals/RecordPaymentModal';
import ConfirmationModal from '../components/ConfirmationModal';

interface ShopsManagementPageProps {}

const ShopsManagementPage: React.FC<ShopsManagementPageProps> = () => {
  const { theme } = useTheme();
  
  // Set page title
  useTitle('Shops Management');
  
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalShops, setTotalShops] = useState(0);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchShops();
      } else {
        setCurrentPage(1); // This will trigger fetchShops via the other useEffect
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchShops();
  }, [currentPage, statusFilter]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        per_page: 10,
      };
      
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      console.log('Fetching shops with params:', params);
      const response = await shopService.getShops(params);
      console.log('Shops response:', response);
      
      // Handle both paginated and non-paginated responses
      if (response.data) {
        setShops(Array.isArray(response.data) ? response.data : []);
        setTotalPages(response.last_page || 1);
        setTotalShops(response.total || 0);
      } else if (Array.isArray(response)) {
        setShops(response);
        setTotalPages(1);
        setTotalShops(response.length);
      } else {
        setShops([]);
        setTotalPages(1);
        setTotalShops(0);
      }
    } catch (error: any) {
      console.error('Error fetching shops:', error);
      console.error('Error response:', error.response?.data);
      setShops([]);
      setTotalPages(1);
      setTotalShops(0);
      customToast.error(error.response?.data?.message || 'Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchShops();
  };

  const handleViewDetails = async (shop: Shop) => {
    try {
      // Fetch full shop details with stats
      const fullShopDetails = await shopService.getShop(shop.id);
      setSelectedShop(fullShopDetails);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching shop details:', error);
      customToast.error('Failed to load shop details');
    }
  };

  const handleActivate = async (shop: Shop) => {
    try {
      await shopService.activateShop(shop.id);
      customToast.success(`${shop.shop_name} has been activated`);
      fetchShops();
    } catch (error) {
      console.error('Error activating shop:', error);
      customToast.error('Failed to activate shop');
    }
  };

  const handleDelete = async (shop: Shop) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Shop',
      message: `Are you sure you want to delete ${shop.shop_name}? This action cannot be undone and all associated data will be permanently removed.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await shopService.deleteShop(shop.id);
          customToast.success(`${shop.shop_name} has been deleted`);
          fetchShops();
        } catch (error) {
          console.error('Error deleting shop:', error);
          customToast.error('Failed to delete shop');
        }
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      suspended: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.suspended}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6 p-2 sm:p-4 lg:p-0">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Shops Management
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage all shops, subscriptions, and billing
              {totalShops > 0 && ` • ${totalShops} total shops`}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className={`flex items-center rounded-xl overflow-hidden border ${
              theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
            }`}>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-all duration-200 ${
                  viewMode === 'list'
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-all duration-200 ${
                  viewMode === 'grid'
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Grid View"
              >
                <Grid3x3 size={18} />
              </button>
            </div>

            {/* Add Shop Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
              title="Add New Shop"
            >
              <Plus size={16} />
              <span className="text-sm">Add Shop</span>
            </button>
          </div>
        </div>

        {/* Search and Filter Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Search Bar */}
          <div className="lg:col-span-8">
            <div className="relative">
              <Search size={18} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder="Search shops..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="lg:col-span-4">
            <div className="relative">
              <Filter className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-600 text-white focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
              >
                <option value="all">All Status</option>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Shops Table/Grid */}
      <div className={`rounded-2xl border overflow-hidden ${
        theme === 'dark'
          ? 'bg-slate-800 border-slate-700'
          : 'bg-white border-gray-200'
      }`}>
        {loading ? (
          <div className="p-8 sm:p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Loading shops...
            </p>
          </div>
        ) : shops.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <Store className={`mx-auto h-12 w-12 mb-3 ${
              theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
            }`} />
            <p className={`text-sm font-medium ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              No shops found
            </p>
            <p className={`mt-1 text-xs ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first shop'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Shop
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Payment
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Expires
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {shops.map((shop) => (
                  <tr key={shop.id} className={theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                          theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                        }`}>
                          <Store className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium truncate max-w-48 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={shop.shop_name}>
                            {shop.shop_name}
                          </div>
                          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            ID: #{shop.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {shop.owner_name}
                      </div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {shop.owner_email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(shop.subscription_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentBadge(shop.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {shop.subscription_status === 'trial' && shop.trial_ends_at
                          ? format(new Date(shop.trial_ends_at), 'MMM dd, yyyy')
                          : shop.subscription_ends_at
                          ? format(new Date(shop.subscription_ends_at), 'MMM dd, yyyy')
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(shop)}
                          className={`p-2 rounded-lg ${
                            theme === 'dark'
                              ? 'text-blue-400 hover:bg-gray-700'
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedShop(shop);
                            setShowEditModal(true);
                          }}
                          className={`p-2 rounded-lg ${
                            theme === 'dark'
                              ? 'text-green-400 hover:bg-gray-700'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {shop.subscription_status === 'suspended' && (
                          <button
                            onClick={() => handleActivate(shop)}
                            className={`p-2 rounded-lg ${
                              theme === 'dark'
                                ? 'text-green-400 hover:bg-gray-700'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title="Activate"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedShop(shop);
                            setShowRecordPaymentModal(true);
                          }}
                          className={`p-2 rounded-lg ${
                            theme === 'dark'
                              ? 'text-purple-400 hover:bg-gray-700'
                              : 'text-purple-600 hover:bg-purple-50'
                          }`}
                          title="Record Payment"
                        >
                          <DollarSign className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(shop)}
                          className={`p-2 rounded-lg ${
                            theme === 'dark'
                              ? 'text-red-400 hover:bg-gray-700'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid View */
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shops.map((shop) => (
              <div
                key={shop.id}
                className={`rounded-xl border p-5 transition-all duration-200 hover:shadow-lg ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 hover:border-blue-500'
                    : 'bg-white border-gray-200 hover:border-blue-400'
                }`}
              >
                {/* Shop Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center ${
                      theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                    }`}>
                      <Store className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <h3 className={`text-sm font-semibold truncate max-w-40 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={shop.shop_name}>
                        {shop.shop_name}
                      </h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        ID: #{shop.id}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {getStatusBadge(shop.subscription_status)}
                  {getPaymentBadge(shop.payment_status)}
                </div>

                {/* Owner Info */}
                <div className={`mb-4 pb-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    OWNER
                  </p>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {shop.owner_name}
                  </p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {shop.owner_email}
                  </p>
                </div>

                {/* Expiration Date */}
                <div className="mb-4">
                  <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {shop.subscription_status === 'trial' ? 'TRIAL EXPIRES' : 'SUBSCRIPTION EXPIRES'}
                  </p>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {shop.subscription_status === 'trial' && shop.trial_ends_at
                      ? format(new Date(shop.trial_ends_at), 'MMM dd, yyyy')
                      : shop.subscription_ends_at
                      ? format(new Date(shop.subscription_ends_at), 'MMM dd, yyyy')
                      : 'N/A'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleViewDetails(shop)}
                    className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-all ${
                      theme === 'dark'
                        ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                    title="View Details"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    <span>View</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedShop(shop);
                      setShowEditModal(true);
                    }}
                    className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-all ${
                      theme === 'dark'
                        ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                    title="Edit"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    <span>Edit</span>
                  </button>
                  {shop.subscription_status === 'suspended' && (
                    <button
                      onClick={() => handleActivate(shop)}
                      className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-all ${
                        theme === 'dark'
                          ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                      title="Activate"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      <span>Activate</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedShop(shop);
                      setShowRecordPaymentModal(true);
                    }}
                    className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-all ${
                      theme === 'dark'
                        ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50'
                        : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                    }`}
                    title="Record Payment"
                  >
                    <DollarSign className="h-3.5 w-3.5" />
                    <span>Payment</span>
                  </button>
                  <button
                    onClick={() => handleDelete(shop)}
                    className={`p-2 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                      theme === 'dark'
                        ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        : 'bg-red-50 text-red-600 hover:bg-red-100'
                    }`}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-4 sm:px-6 py-4 border-t ${
            theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
          }`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
              >
                Previous
              </Button>
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateShopModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchShops}
      />
      <EditShopModal
        isOpen={showEditModal}
        shop={selectedShop}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchShops}
      />
      <ViewShopDetailsModal
        isOpen={showDetailsModal}
        shop={selectedShop}
        onClose={() => setShowDetailsModal(false)}
      />
      <RecordPaymentModal
        isOpen={showRecordPaymentModal}
        shop={selectedShop}
        onClose={() => setShowRecordPaymentModal(false)}
        onSuccess={fetchShops}
      />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        onConfirm={() => {
          confirmationModal.onConfirm();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        }}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ShopsManagementPage;
